<template>
    <!-- height:100% works because the parent column now has an explicit pixel height -->
    <div v-if="internalItem" style="height: 100%; display: flex; flex-direction: column;">
        <ModalMenuItemGallery
            ref="modalMenuItemGallery"
            :images="resources"
            :selected="internalItem.icon"
            @select="handleSelectIcon"
        />
        <!-- Fixed header: title + close (form edits apply live; no Save/Cancel
             buttons — the menu-level Save/Cancel handles persistence). -->
        <div class="flex-shrink-0 flex items-center justify-between gap-3 pt-4 pb-3"
             style="background: #F4F4FA; z-index: 10;">
            <h3 class="m-0 font-semibold truncate">
                {{ action === 'create' ? $t('apps.configurator.manage-item.title') : internalItem.name }}
            </h3>
            <button
                @click.stop="close"
                class="flex-shrink-0 cursor-pointer bg-transparent border-0 outline-none p-1 rounded"
                :aria-label="$t('apps.configurator.menu-list.cancel')"
            >
                <feather-icon icon="XIcon" svgClasses="h-5 w-5 stroke-current text-grey-darker" />
            </button>
        </div>

        <!-- Scrollable body: flex:1 fills remaining height, overflows with scroll -->
        <div style="flex: 1; overflow-y: auto; min-height: 0; padding: 0 16px 16px;">

        <!-- Main -->
        <div class="mt-12">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.type_and_visibility') }}</h3>

            <div class="flex flex-col vx-card p-8">
                <div class="w-full">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.type') }}</LabelForm>
                    <vs-select
                        :disabled="readOnly || this.internalItem.submenu_items.length > 0"
                        class="w-96 border-grey-light"
                        v-model="internalItem.item_template_uuid"
                    >
                        <vs-select-item :key="templatesOpt.value" :value="templatesOpt.value" :text="templatesOpt.label" v-for="templatesOpt in templatesOpts"/>
                    </vs-select>

                    <Alert v-if="!readOnly && this.internalItem.submenu_items.length > 0" type="warning" class="mt-4">
                        {{ $t('apps.configurator.manage-item.cannot_change_template') }}
                    </Alert>
                </div>

                <div v-if="template">
                    <LabelForm font-size="text-sm" margins="mb-0 mt-3">{{ $t('apps.configurator.manage-item.name') }}</LabelForm>
                    <vs-input :disabled="readOnly" type="text" maxlength="50"
                              class="input-modal w-96 border-grey-light" v-model="internalItem.name" :danger="!internalItem.name"/>
                </div>

                <div>
                    <LabelForm font-size="text-sm" margins="mb-0 mt-3">{{ $t('apps.configurator.manage-item.published') }}</LabelForm>
                    <vs-select :disabled="readOnly" class="w-20 border-grey-light" v-model="internalItem.published" required>
                        <vs-select-item :key="publishedOpt.value" :value="publishedOpt.value" :text="publishedOpt.label" v-for="publishedOpt in publishedOpts"/>
                    </vs-select>
                </div>
            </div>
        </div>
        <!-- Main -->

        <!-- Button -->
        <div class="mt-12" v-if="template && hasAnyTemplatePresetAttributes(['icon', 'width'])">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.button') }}</h3>

            <div class="vx-card p-8 flex flex-col gap-3">

                <div v-if="hasTemplatePresetAttribute('width')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.columns') }}</LabelForm>
                    <vs-select :disabled="menu.type === 'tab' || readOnly" class="w-20 border-grey-light" v-model="internalItem.width" required>
                        <vs-select-item :key="columns" :value="columns" :text="columns" v-for="columns in columnsOptions"/>
                    </vs-select>
                </div>

                <div v-if="hasTemplatePresetAttribute('icon')" class="w-full">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.icon') }}</LabelForm>
                    <div class="flex flex-row items-center gap-2">
                        <vs-input type="text" maxlength="50"
                                  class="input-modal w-full border-grey-light"
                                  v-model="internalItem.icon"
                                  disabled
                        />
                        <div v-if="!readOnly" class="flex flex-row items-center gap-2">
                            <button class="sk-btn sk-btn-small sk-btn-primary rounded-lg btn-border p-2" @click="openGallery">
                                <feather-icon icon="ImageIcon" svgClasses="h-6 w-6 stroke-current text-primary" class="align-middle"/>
                            </button>
                            <button class="sk-btn sk-btn-small sk-btn-danger rounded-lg btn-border p-2" @click="clearIcon">
                                <feather-icon icon="XIcon" svgClasses="h-6 w-6 stroke-current text-danger" class="align-middle"/>
                            </button>
                        </div>
                    </div>

                    <div v-if="imagesObject">
                        <Alert
                            v-if="imagesObject.multiLanguage && imagesObject.missingImages.length > 0"
                            type="warning"
                            class="mt-4"
                        >
                            <p class="font-medium mb-2">{{ $t('apps.configurator.preview.image_missing') }}:</p>
                            <ul>
                                <li v-for="image in imagesObject.missingImages" :key="image">{{ image }}</li>
                            </ul>
                        </Alert>

                        <Gallery :asset-list="imagesObject.images" />
                    </div>
                </div>

                <div class="languages-inputs">
                    <LabelForm font-size="text-base" margins="mb-2">{{ $t('apps.configurator.manage-item.item_title') }}</LabelForm>
                    <DynamicForm
                        v-model="languageValue"
                        @input="getDinamicInputsLanguages"
                        :languages="languages.map(lang => lang.acronym)"
                        :columns=2
                        :isReadOnly="readOnly"
                    />
                </div>
            </div>
        </div>
        <!-- Button -->

        <!-- Configuration -->
        <div class="mt-12" v-if="showConfiguration">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.configuration') }}</h3>

            <div class="vx-card p-8">
                <DynamicForm
                    v-model="configurationValue"
                    @input="getDinamicInputsConfiguration"
                    @refresh="refreshTemplate"
                    :languages="languages.map(lang => lang.acronym)"
                    :columns=1
                    :isReadOnly="readOnly"
                    :condensed-selects="!hasAnyTemplatePresetAttributes(['screen_name', 'flag', 'section'])"
                />
            </div>
        </div>
        <!-- Configuration -->

        <!-- Metadata -->
        <div class="mt-12" v-if="template && hasAnyTemplatePresetAttributes(['screen_name', 'flag', 'section'])">
            <h3 class="font-semibold text-base mb-4">{{ $t('apps.configurator.manage-item.metadata') }}</h3>

            <div class="vx-card p-8 flex flex-col gap-3">
                <div v-if="hasTemplatePresetAttribute('screen_name_category') && screenNameCategories.length > 0">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.screen-name-category') }}</LabelForm>
                    <vs-select :disabled="readOnly" class="w-full border-grey-light"
                               v-model="internalItem.screen_name_category" required>
                        <vs-select-item :key="category" :value="category" :text="category" v-for="category in screenNameCategories"/>
                    </vs-select>
                </div>

                <div v-if="hasTemplatePresetAttribute('screen_name')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.screen-name') }}</LabelForm>
                    <vs-input :disabled="readOnly" type="text" maxlength="50"
                              class="input-modal w-full border-grey-light" v-model="internalItem.screen_name" />
                </div>

                <div v-if="hasTemplatePresetAttribute('flag')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.flag') }}</LabelForm>
                    <vs-input disabled type="text" maxlength="50"
                              class="input-modal w-full border-grey-light" v-model="internalItem.flag" />
                </div>

                <div v-if="hasTemplatePresetAttribute('section')">
                    <LabelForm font-size="text-sm" margins="mb-0">{{ $t('apps.configurator.manage-item.section') }}</LabelForm>
                    <vs-input disabled type="text" maxlength="50" class="w-full border-grey-light input-modal"
                              v-model="internalItem.section" />
                </div>
            </div>
        </div>
        <!-- Metadata -->

        </div><!-- /scrollable body -->

    </div>
</template>

<script>
import LabelForm from '@/modules/Shared/Components/form/LabelForm'
import DynamicForm from "@/modules/Shared/Components/form/DynamicForm";
import SKModal from "@/modules/Shared/Components/modal/SKModal.vue";
import Gallery from "@/modules/Apps/Components/resources/gallery/Gallery.vue";
import emptyAlert from "@/modules/Shared/Components/alert/emptyAlert.vue";
import ModalMenuItemGallery from "@/modules/Apps/Components/configurator/ModalMenuItemGallery.vue";
import Alert from "@/modules/Shared/Components/alert/Alert.vue";
import ConfiguratorItemTemplateService from "@/modules/Apps/Services/Configurator/ConfiguratorItemTemplateService";
import loader from "@/modules/Shared/Mixins/loader";
import { mapGetters } from 'vuex'

export default {
    name: 'ManageMenuItemForm',
    mixins: [loader],
    props: {
        menu: {
            type: Object,
            required: true
        },
        layout: {
            type: Object|null,
            required: true,
            default: null
        },
        templates: {
            type: Array,
            required: true
        },
        languages: {
            type: Array,
            required: true
        },
        resources: {
            type: Array,
            required: true
        },
        readOnly: {
            type: Boolean,
            required: false,
            default: false,
        }
    },
    components: {
        Alert,
        ModalMenuItemGallery,
        emptyAlert,
        Gallery,
        SKModal,
        LabelForm,
        DynamicForm
    },
    watch: {
        'internalItem.item_template_uuid': {
            async handler(current, old) {
                if (!current || current === old || this.isInitializingFromUpdate) {
                    return
                }
                await this.changeTemplate(current)
            }
        }
    },
    methods: {
        clearIcon() {
            this.internalItem.icon = ''
            this.$emit('loaded')
        },
        handleSelectIcon(icon) {
            this.internalItem.icon = icon.key
            this.$emit('loaded')
        },
        openGallery() {
            this.$refs.modalMenuItemGallery.open()
        },
        close() {
            this.$emit('close')
        },
        async changeTemplate(templateUuid) {
            const template = await this.loadItemTemplate(templateUuid)

            // Live-binding: mirror the template's default configuration into the
            // bound item so the grid (e.g., home- vs native-submenu detection)
            // reflects the choice immediately. Empty config is an object {} (not
            // an array []) so DynamicForm's localized fields can mutate keys on it.
            this.internalItem.configuration = template.configuration === null || Array.isArray(template.configuration)
                ? {}
                : { ...template.configuration }
            // configurationValue.values MUST share the same reference as
            // internalItem.configuration: DynamicForm's localized fields mutate
            // `value.values` in place without emitting an input event, so the
            // mutation only reaches the bound item if they are the same object
            // (mirrors the edit path in initialize()).
            this.configurationValue = template.configuration_template !== null && template.configuration_template !== false
                ? { template: template.configuration_template, values: this.internalItem.configuration }
                : { values: this.internalItem.configuration }

            if (!template.preset.includes('icon')) {
                this.internalItem.icon = null
            }

            if (!this.internalItem.name) {
                this.internalItem.name = template.titles.en
            }

            this.internalItem.flag = template.flag
            this.internalItem.section = template.section
            this.resolveScreenNameCategory(template)

            if (!this.template) {
                this.languageValue.values = template.titles
                // Live-binding: the form's title fields are now populated, but
                // the grid reads `item.titles`. Mirror the template's titles
                // into the bound item so the visual grid updates in real time.
                this.internalItem.titles = { ...template.titles }
                this.internalItem.icon = template.icon
                this.internalItem.screen_name = template.screen_name
            }

            this.template = template
            this.modifyItemWidthBasedOnTemplate()

            this.$emit('loaded')
        },
        // Catalogue a template offers, always as an array: the API models the
        // empty catalogue as NULL, so NULL and [] mean the same thing.
        templateScreenNameCategories(template) {
            return template && Array.isArray(template.screen_name_categories)
                ? template.screen_name_categories
                : []
        },
        // The category catalogue lives on the item template, so a template
        // change can leave the current value outside the new list. It is
        // resolved here the same way `icon` is reset when the new preset
        // drops it: kept when still valid, first value otherwise, and null
        // when the new template offers no categories at all.
        resolveScreenNameCategory(template) {
            const categories = template.preset.includes('screen_name_category')
                ? this.templateScreenNameCategories(template)
                : []

            if (categories.length === 0) {
                this.internalItem.screen_name_category = null
                return
            }

            if (!categories.includes(this.internalItem.screen_name_category)) {
                this.internalItem.screen_name_category = categories[0]
            }
        },
        async loadItemTemplate(itemTemplateUuid) {
            let template = null

            await this.loadAndIgnoreError(async () => {
                const resortUuid = this.menu.resorts.length ? this.menu.resorts[0] : null
                template = await ConfiguratorItemTemplateService.getItemTemplate(itemTemplateUuid, this.$route.params.uuid, resortUuid, this.$route.params.versionUuid)
            })

            return template
        },
        async refreshTemplate() {
            this.template = await this.loadItemTemplate(this.template.uuid)
            this.configurationValue.template = this.template.configuration_template
        },
        getDinamicInputsLanguages(dynamicInputs) {
            this.languageValue = dynamicInputs
            // Live-sync: titles flow straight into the bound item so the grid
            // re-renders cell labels in real time.
            if (this.internalItem) this.internalItem.titles = dynamicInputs.values
        },
        getDinamicInputsConfiguration(dynamicInputs) {
            for (let key in dynamicInputs.values) {
                if (dynamicInputs.values.hasOwnProperty(key) && typeof dynamicInputs.values[key] === 'boolean') {
                    dynamicInputs.values[key] = dynamicInputs.values[key].toString();
                }
            }
            this.configurationValue = dynamicInputs
            // Live-sync: configuration mirrors into the bound item. Empty config
            // is {} (object) not [] (array) — see initialize() for why.
            if (this.internalItem) {
                this.internalItem.configuration = dynamicInputs.values === null ? {} : dynamicInputs.values
            }
        },
        getImageAcronym(image) {
            const imageName = image.split('/').pop();
            const imageNameWithoutExtension = imageName.split('.')[0];
            const candidates = this.spotlioLanguages.filter(lang => imageNameWithoutExtension.endsWith(`_${lang.acronym}`))
            return candidates.length > 0 ? candidates[0].acronym : null
        },
        hasTemplatePresetAttribute(attribute) {
            if (!this.template) {
                return false
            }

            // Webview template should show/hide width attribute based on layout
            if (attribute === 'width' && !this.layout) {
                return false
            }

            return this.template.preset.includes(attribute)
        },
        hasAnyTemplatePresetAttributes(attributesArray) {
            return attributesArray.some(attribute => this.hasTemplatePresetAttribute(attribute))
        },
        modifyItemWidthBasedOnTemplate() {
            // Webview template should show/hide width attribute based on layout
            if (!this.layout) {
                return
            }

            if (!this.template.preset.includes('width')) {
                this.internalItem.width = this.layout.values.columns_number
            }
        },
        // Live-binding mode: `item` is the actual reactive object from
        // menu.items (NOT a clone). The form mutates it directly so every
        // change reaches the grid immediately.
        async initialize(item, mode = 'edit') {
            this.template = null
            const userLang = this.$store.state.AppActiveUser.language

            this.languageValue = {
                template: this.languages.map(lang => ({
                    key: lang.acronym,
                    input: "textfield",
                    title: { [userLang]: this.spotlioLanguages.find(lang2 => lang2.acronym === lang.acronym).name },
                    required: true
                }))
            }

            this.action = mode === 'create' ? 'create' : 'update'
            this.internalItem = item

            if (mode === 'create' && !item.item_template_uuid) {
                // Fresh placeholder — template hasn't been chosen yet. Picking
                // one will trigger changeTemplate via the item_template_uuid watch.
                this.configurationValue = { values: {} }
                this.languageValue.values = {}
            } else {
                this.isInitializingFromUpdate = true
                this.template = await this.loadItemTemplate(this.internalItem.item_template_uuid)
                // Normalize configuration to an object: legacy items may have
                // configuration: [] (empty array) which silently loses any
                // localized field values because JSON.stringify drops string
                // keys on arrays. DynamicForm's localized handler mutates the
                // shared reference without emitting, so the values must live
                // on a plain object to survive a save round-trip.
                if (this.internalItem.configuration === null
                    || Array.isArray(this.internalItem.configuration)) {
                    this.internalItem.configuration = {}
                }
                this.configurationValue = {
                    template: this.template.configuration_template,
                    values: this.internalItem.configuration,
                }
                this.languageValue.values = this.internalItem.titles
                this.isInitializingFromUpdate = false
            }

            this.$emit('loaded');
        }
    },
    data() {
        return {
            isInitializingFromUpdate: false,
            action: 'create',
            internalItem: null,
            configurationValue: { values: {} },
            languageValue: {},
            template: null,
            publishedOpts: [
                { label: this.$i18n.t('yes'), value: true },
                { label: this.$i18n.t('no'), value: false },
            ],
            widthOpts: [
                { label: this.$t('apps.configurator.manage-item.with-1-column'), value: 1 },
                { label: this.$t('apps.configurator.manage-item.with-2-column'), value: 2 },
                { label: this.$t('apps.configurator.manage-item.with-3-column'), value: 3 },
            ],
        }
    },
    computed: {
        ...mapGetters(['spotlioLanguages']),
        screenNameCategories() {
            return this.templateScreenNameCategories(this.template)
        },
        columnsOptions() {
            return Array(this.layout.values.columns_number)
                .fill()
                .map((_, index) => index + 1)
        },
        imagesObject() {
            if (!this.internalItem || !this.internalItem.icon) {
                return null
            }

            const singleImageRegex = new RegExp(`/${this.internalItem.icon}\\.`)
            let images = this.resources.filter(resource => resource.match(singleImageRegex))

            if (images.length > 0) {
                return {
                    multiLanguage: false,
                    images: images
                }
            }

            const acronyms = this.languages.map(lang => lang.acronym)
            const multilanguageImageRegex = new RegExp(`/${this.internalItem.icon}_(${acronyms.join('|')})\\.`)
            images = this.resources.filter(resource => resource.match(multilanguageImageRegex))

            if (images.length > 0) {
                const missingLanguages = acronyms.filter(acronym => !images.map(this.getImageAcronym).includes(acronym))
                const extension = images[0].split('.').pop()
                return {
                    multiLanguage: true,
                    images: images,
                    missingImages: missingLanguages.map(acronym => `${this.internalItem.icon}_${acronym}.${extension}`),
                }
            }

            return null
        },
        templatesOpts() {
            if (this.templates !== null) {
                return this.templates.map(template => {
                    return {
                        label: template.name.scalar !== undefined
                            ? this.$t(template.name.scalar)
                            : template.name[this.$store.getters.applicationLanguage],
                        value: template.uuid
                    }
                }).sort((a, b) => {
                    return a.label < b.label ? -1 : a.label > b.label ? 1 : 0
                })
            }
        },
        showConfiguration() {
            if (!this.template) {
                return false
            }

            return this.template.configuration_template !== null
                && this.template.configuration_template.some(field => !field.hasOwnProperty('show') || field.show !== false)
        }
    }
}
</script>
